import type { Item } from '../dto/searchResultHit';
import { getFrameDurationFromFrameRateApproximation } from '../util/framerate';
import type { FuzzyValue } from './fuzzyNumbers';
import { getTechnicalMetadataValue, TechnicalVideoDataField } from '../util/technicalMetadata';
import type { PlayableArtifactSelectOptions, PlayableArtifact } from '../dto/playableItemDto';
import type { StreamRoleId, CodecString, MediaAvailability } from '../dto/mediaAvailability';
import type { StreamSetPreference } from '../dto/mediaDtos';

export const DEFAULT_FRAMERATE = 30;

export function getMediaFrameRateFromItem(item: Pick<Item, 'mediaFrameRate' | 'technicalMetadata'>) {
  return item.mediaFrameRate || getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalVideoDataField.FrameRate) || null;
}


export function getFrameDuration(item: Pick<Item, 'mediaFrameRate' | 'technicalMetadata'>): FuzzyValue {
  const mediaFrameRate = getMediaFrameRateFromItem(item);
  if (mediaFrameRate == null) {
    return { approximate: 1 / DEFAULT_FRAMERATE, accuracyWarning: 'Frame rate unknown' };
  }

  return getFrameDurationFromFrameRateApproximation(mediaFrameRate);
}


function matchesStreamSet(streamSet: StreamRoleId[], codecsByStreamId: Partial<Record<StreamRoleId, CodecString>>) {
  for (const streamId of streamSet) {
    if (codecsByStreamId[streamId]) {
      return true;
    }
  }
  return false;
}

function selectStreamSet(
  streamSetPreference: StreamSetPreference,
  codecsByStreamId: Partial<Record<StreamRoleId, CodecString>> | undefined,
): StreamRoleId[] | null {
  return streamSetPreference.find(ss => matchesStreamSet(ss, codecsByStreamId ?? { })) || null;
}


/**
 * We only look at the file extension to decide that it may be playable.
 * In reality, it also depends on the codecs etc to actually play in the browser,
 * But most of the common codecs in these containers are supported, and therefore
 * we skip more complicated checks to keep it simple for common scenarios.
 */
export function fileMayBePlayable(originalFileName: string | undefined | null): boolean {
  const possiblyPlayableHighResExtensions = ['.mp4', '.webm', '.ogg'];
  if (!originalFileName) {
    return false;
  }

  const extensionStart = originalFileName.lastIndexOf('.');
  if (extensionStart === -1) {
    return false;
  }
  const ext = originalFileName.slice(extensionStart).toLowerCase();
  if (!possiblyPlayableHighResExtensions.includes(ext)) {
    return false;
  }

  return true;
}


function isEditProxyBrowserPlayableAndAvailable(
  editProxyFormat: string | null,
  editProxyUrl: string | null,
) {
  return editProxyFormat !== 'prores' && editProxyUrl;
}


export function selectPlayableArtifact(
  item: {
    media: MediaAvailability | null,
    editProxyUrl: string | null,
    editProxyFormat: string | null,
    proxyUrl: string | null,
    highresUrl: string | null,
    originalFileName: string | null,
  },
  options: PlayableArtifactSelectOptions,
): PlayableArtifact | null {
  // item.media will be present for segmented media. It used to mean live items
  // but now this may also happen for other ingests workflows that produce ts
  // segmented proxies, like for instance Kelda watchfolders
  if (item.media) {
    // In the scheduler, the user may want to open in the quick preview a recording
    // that has not started yet and therefore without media
    const streamSet = selectStreamSet(options.streamSetPreference, item.media.codecsByStreamId);
    if (streamSet) {
      return { type: 'stream', streamSet };
    } else {
      return null;
    }
  }
  if (item.editProxyUrl && isEditProxyBrowserPlayableAndAvailable(item.editProxyFormat, item.editProxyUrl) && options.useEditProxy) {
    return { type: 'flatEditProxy', url: item.editProxyUrl };
  }
  if (item.proxyUrl) {
    return { type: 'flatWebProxy', url: item.proxyUrl };
  }
  if (item.editProxyUrl && isEditProxyBrowserPlayableAndAvailable(item.editProxyFormat, item.editProxyUrl)) {
    return { type: 'flatEditProxy', url: item.editProxyUrl };
  }
  if (item.highresUrl && fileMayBePlayable(item.originalFileName)) {
    return { type: 'flatHighRes', url: item.highresUrl };
  }
  return null;
}
